import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { nodeMeta, ORBIT_COLORS, type NetworkNode, type NetworkView } from '@rhwb/types';
import { addFamily, addRecruit, MIN_SEARCH, setOutsideReferrer, setReferrer, useNetworkSearch } from '../../lib/network';

interface NetworkPickerDialogProps {
  open: boolean;
  /**
   * `referrer`: "Who brought you?" — one pick closes the dialog.
   * `recruit`: "Who did you bring?" — stays open for several adds.
   * `family`: "Add family" — stays open for several adds.
   */
  mode: 'referrer' | 'recruit' | 'family';
  /** Your own node, so recruits you've already claimed read "Added". */
  me: NetworkNode | null;
  /** Your referrer chain, nearest first. */
  ancestors: NetworkNode[];
  /** The name on file for a referrer outside RHWB, if any. */
  outsideReferrer?: string | null;
  /** Already answered "I found RHWB myself". */
  foundSelf?: boolean;
  onClose: () => void;
  /** The server's fresh view after each save. */
  onSaved: (view: NetworkView) => void;
}

type Status = { text: string; tone: 'done' | 'muted' } | undefined;

/**
 * Search any runner, current or past, by name. Rows carry city and first
 * season so same-name runners can be told apart without showing an email.
 * Same rules as the mobile sheets: first answer wins for recruits, and nobody
 * above you in your chain can be added below you.
 *
 * Referrer mode also takes a typed name for someone who isn't in RHWB, from
 * the footer or from the bottom of the search results.
 */
const NetworkPickerDialog: React.FC<NetworkPickerDialogProps> = ({
  open,
  mode,
  me,
  ancestors,
  outsideReferrer = null,
  foundSelf = false,
  onClose,
  onSaved,
}) => {
  const [query, setQuery] = useState('');
  const { results, searching, error: searchError } = useNetworkSearch(query, mode);
  const [saving, setSaving] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const family = mode === 'family';
  // Recruit and family both stay open for several adds.
  const recruit = mode === 'recruit' || family;
  const accent = family ? ORBIT_COLORS.family : recruit ? ORBIT_COLORS.child : ORBIT_COLORS.referrer;
  const current = ancestors[0] ?? null;
  const currentName = current?.name ?? outsideReferrer;
  // Typing a name for someone outside RHWB, in place of the search.
  const [naming, setNaming] = useState(false);
  const [outsideName, setOutsideName] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setNaming(false);
    setOutsideName('');
    setAdded(new Set());
    setError(null);
    setSaving(null);
  }, [open]);

  const run = async (key: string, call: () => Promise<NetworkView>, after?: () => void) => {
    setSaving(key);
    setError(null);
    try {
      onSaved(await call());
      after?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const pick = (node: NetworkNode) =>
    recruit
      ? run(
          node.id,
          () => (family ? addFamily(node.id) : addRecruit(node.id)),
          () => setAdded((prev) => new Set(prev).add(node.id))
        )
      : run(node.id, () => setReferrer(node.id), onClose);

  const statusOf = (node: NetworkNode): Status => {
    if (!recruit) return current?.id === node.id ? { text: 'Your referrer', tone: 'done' } : undefined;
    if (family) {
      if (added.has(node.id) || node.inFamily === 'yours') return { text: 'In your family', tone: 'done' };
      if (node.inFamily === 'other') return { text: 'In another family', tone: 'muted' };
      return undefined;
    }
    if (added.has(node.id) || (me && node.referredBy?.id === me.id)) return { text: 'Added', tone: 'done' };
    if (current?.id === node.id) return { text: 'Brought you in', tone: 'muted' };
    if (ancestors.some((a) => a.id === node.id)) return { text: 'Above you in your chain', tone: 'muted' };
    if (node.referredBy) return { text: `Brought in by ${node.referredBy.name}`, tone: 'muted' };
    return undefined;
  };

  const q = query.trim();

  const startNaming = (prefill: string) => {
    setOutsideName(prefill);
    setError(null);
    setNaming(true);
  };
  const saveOutside = () => {
    const name = outsideName.trim();
    if (name) run('outside', () => setOutsideReferrer(name), onClose);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5, textAlign: 'center', fontWeight: 800 }}>
        {family ? 'Add family' : recruit ? 'Who did you bring to RHWB?' : 'Who brought you to RHWB?'}
      </DialogTitle>
      <DialogContent sx={{ pb: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
          {family
            ? 'Find the family members who run with RHWB. They’ll show next to you in the network.'
            : recruit
              ? 'Add the runners who joined because of you. Add as many as you like.'
              : 'Our club grows by word of mouth. Find the runner who told you about us.'}
        </Typography>

        {naming ? (
          <Box
            component="form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              saveOutside();
            }}
            // As tall as the search, results, answer tiles and footer, so the dialog doesn't jump.
            sx={{ height: 460, display: 'flex', flexDirection: 'column', gap: 1.5 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Not in RHWB? Tell us their name.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Their name"
              value={outsideName}
              onChange={(e) => setOutsideName(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Just a name, no contact details. It shows only to you, and you can change it later if they join.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 'auto' }}>
              <Button onClick={() => setNaming(false)} disabled={!!saving} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                Back to search
              </Button>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={!outsideName.trim() || !!saving}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 999, bgcolor: accent, '&:hover': { bgcolor: accent } }}
              >
                {saving === 'outside' ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputProps={{ 'aria-label': 'Search runners by name' }}
            />

            <Box sx={{ height: recruit ? 280 : 220, overflowY: 'auto', mt: 1 }}>
              {searching && results.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 3 }}>
                  <CircularProgress size={22} sx={{ color: accent }} />
                </Box>
              ) : q.length < MIN_SEARCH ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', pt: 3 }}>
                  Type at least two letters of their name.
                </Typography>
              ) : results.length === 0 ? (
                <>
                  <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', pt: 3 }}>
                    No runners match “{q}”.
                  </Typography>
                  {!recruit && <OutsideRow query={q} accent={accent} disabled={!!saving} onClick={() => startNaming(q)} />}
                </>
              ) : (
                <List dense disablePadding>
                  {results.map((node) => {
                    const status = statusOf(node);
                    const blocked = recruit ? !!status : status?.tone === 'done';
                    return (
                      <ListItemButton
                        key={node.id}
                        onClick={() => pick(node)}
                        disabled={blocked || !!saving}
                        sx={{ borderRadius: 2, '&.Mui-disabled': { opacity: status?.tone === 'done' ? 1 : 0.55 } }}
                      >
                        <ListItemAvatar sx={{ minWidth: 48 }}>
                          <Avatar src={node.picture ?? undefined} sx={{ bgcolor: accent, width: 36, height: 36, fontSize: 13, fontWeight: 700 }}>
                            {node.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={node.name}
                          primaryTypographyProps={{ fontWeight: 700, noWrap: true }}
                          secondary={
                            <>
                              {nodeMeta(node)}
                              {status && (
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    fontWeight: 600,
                                    color: status.tone === 'done' ? ORBIT_COLORS.overflowText : 'text.disabled',
                                  }}
                                >
                                  {status.tone === 'done' && <CheckIcon sx={{ fontSize: 14 }} />}
                                  {status.text}
                                </Box>
                              )}
                            </>
                          }
                        />
                        {saving === node.id && <CircularProgress size={18} sx={{ color: accent }} />}
                      </ListItemButton>
                    );
                  })}
                  {!recruit && !searching && (
                    <OutsideRow query={q} accent={accent} disabled={!!saving} onClick={() => startNaming(q)} />
                  )}
                </List>
              )}
            </Box>

            {/* The two answers that aren't a runner, set apart from the search. */}
            {!recruit && (
              <>
                <Divider sx={{ my: 2, color: 'text.disabled', fontSize: 12, fontWeight: 600 }}>or</Divider>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <ChoiceTile
                    icon={<StarRoundedIcon />}
                    title="I found RHWB myself"
                    caption="Social media, local runs, Google…"
                    accent={accent}
                    selected={foundSelf && !current && !outsideReferrer}
                    loading={saving === 'self'}
                    disabled={!!saving}
                    onClick={() => run('self', () => setReferrer(null, true), onClose)}
                  />
                  <ChoiceTile
                    icon={<PersonAddAltOutlinedIcon />}
                    title="They’re not in RHWB"
                    caption={outsideReferrer && !current ? outsideReferrer : 'Add their name instead'}
                    accent={accent}
                    selected={!!outsideReferrer && !current}
                    disabled={!!saving}
                    onClick={() => startNaming(outsideReferrer ?? q)}
                  />
                </Box>
              </>
            )}
          </>
        )}

        {(error ?? searchError) && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error ?? searchError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
        {naming ? null : recruit ? (
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: accent }}>
            {added.size > 0 ? `Done · ${added.size} added` : 'Done'}
          </Button>
        ) : (
          <>
            {currentName && (
              <Button
                onClick={() => run('clear', () => setReferrer(null), onClose)}
                disabled={!!saving}
                sx={{ textTransform: 'none', color: 'text.secondary' }}
              >
                Remove {currentName}
              </Button>
            )}
            <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              Cancel
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/** One of the two non-runner answers, side by side under the search. */
const ChoiceTile: React.FC<{
  icon: React.ReactNode;
  title: string;
  caption: string;
  accent: string;
  selected: boolean;
  loading?: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ icon, title, caption, accent, selected, loading = false, disabled, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 1.5,
      borderRadius: '14px',
      border: '1.5px solid',
      borderColor: selected ? accent : 'divider',
      bgcolor: selected ? 'rgba(124,58,237,0.06)' : 'background.paper',
      color: 'text.primary',
      font: 'inherit',
      textAlign: 'center',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled && !loading ? 0.6 : 1,
      transition: 'border-color 120ms, background-color 120ms',
      '&:hover:not(:disabled)': { borderColor: accent, bgcolor: 'rgba(124,58,237,0.04)' },
      '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
    }}
  >
    {selected && <CheckIcon sx={{ position: 'absolute', top: 6, right: 6, fontSize: 16, color: accent }} />}
    <Box sx={{ color: accent, display: 'flex', height: 24, alignItems: 'center' }}>
      {loading ? <CircularProgress size={18} sx={{ color: accent }} /> : icon}
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.25 }}>{title}</Typography>
    <Typography
      variant="caption"
      // Up to three lines, so a long caption wraps rather than cutting off on a phone.
      sx={{
        color: 'text.secondary',
        lineHeight: 1.3,
        maxWidth: '100%',
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflowWrap: 'anywhere',
      }}
    >
      {caption}
    </Typography>
  </Box>
);

/** The search fallback: "Can't find them?" turns the query into a typed name. */
const OutsideRow: React.FC<{ query: string; accent: string; disabled: boolean; onClick: () => void }> = ({
  query,
  accent,
  disabled,
  onClick,
}) => (
  <ListItemButton onClick={onClick} disabled={disabled} sx={{ borderRadius: 2, mt: 0.5, border: '1px dashed', borderColor: 'divider' }}>
    <ListItemAvatar sx={{ minWidth: 48 }}>
      <Avatar sx={{ bgcolor: 'white', color: accent, border: `1.5px dashed ${accent}`, width: 36, height: 36 }}>
        <PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />
      </Avatar>
    </ListItemAvatar>
    <ListItemText
      primary="Can’t find them? They may not be in RHWB."
      primaryTypographyProps={{ fontWeight: 700, fontSize: 14 }}
      secondary={`Add “${query}” by name instead`}
    />
  </ListItemButton>
);

export default NetworkPickerDialog;
